// stores/lingoStore.ts
import { defineStore } from "pinia";
import api from "@/api";
import {
  LingoDataResponse, StudentLingoData, CustomList, CustomListResponse,
  GenerateCustomListPayload, GenerateCustomListData, GenerateCustomListResponse,
  SaveCustomListPayload, SaveCustomListResponse,
  AssignListStudent, AssignListStudentsResponse, AssignListStudentsFilters,
  AssignActionResponse, AssignUpdatedStatus,
  CustomListStatusData, CustomListStatusResponse
} from "@/interfaces/lingo.interfaces";

export const useLingoStore = defineStore({
  id: "lingoStore",
  state: () => ({
    studentLingoData: [] as StudentLingoData[],
    customLists: [] as CustomList[],
    listStudents: [] as AssignListStudent[],
    listStudentsMeta: { total: 0, page: 1, limit: 50, isLastPage: true },
    loader: {
      fetchStudentLingoData: false,
      fetchWord: false,
      fetchCustomLists: false,
      generateCustomList: false,
      saveCustomList: false,
      fetchListStudents: false,
      assignList: false,
      unassignList: false,
    },
    wordCache: {} as Record<string, any>,
    lingoEngagementTime: {} as Record<string, { classworkSeconds: number; homeworkSeconds: number }>,
  }),

  getters: {
    getStudentLingoData(): StudentLingoData[] {
      return this.studentLingoData;
    },

    getLingoDataByStudentId: (state) => (studentId: string) => {
      return state.studentLingoData.find(data => data.userId === studentId);
    },

    getWordByName: (state) => (wordName: string) => {
      return state.wordCache[wordName] ?? null;
    },

    getEngagementTimeByStudentId: (state) => (studentId: string) => {
      return state.lingoEngagementTime[studentId] ?? null;
    },

    getSpellingMasteryPercentage: (state) => (studentId: string) => {
      const studentData = state.studentLingoData.find(data => data.userId === studentId);
      if (!studentData || studentData.spellingTotalCount === 0) return 0;
      return Math.round((studentData.spellingMasteredCount / studentData.spellingTotalCount) * 100);
    },

    getVocabularyMasteryPercentage: (state) => (studentId: string) => {
      const studentData = state.studentLingoData.find(data => data.userId === studentId);
      if (!studentData || studentData.vocabularyTotalCount === 0) return 0;
      return Math.round((studentData.vocabularyMasteredCount / studentData.vocabularyTotalCount) * 100);
    },
  },

  actions: {
    /**
     * Fetch lingo data for multiple students
     */
    async fetchStudentLingoData(payload: { studentIds: string[] }): Promise<boolean> {
      this.loader.fetchStudentLingoData = true;

      try {
        const response = await api.post<LingoDataResponse>(
          "/v2/lingo-v2/progress-data/students-data",
          payload
        );

        if (response.status === 200 && response.data.success) {
          const incoming = Array.isArray(response.data.data) ? response.data.data : [];
          // Merge by userId instead of replacing the whole array
          const byId = new Map(this.studentLingoData.map(data => [data.userId, data]));
          incoming.forEach(data => byId.set(data.userId, data));
          this.studentLingoData = [...byId.values()];
          return true;
        } else {
          throw new Error("Failed to fetch student lingo data.");
        }
      } catch (error) {
        console.error("Error fetching student lingo data: ", error);
        return false;
      } finally {
        this.loader.fetchStudentLingoData = false;
      }
    },

    /**
     * Fetch a single word by id from the API and cache it.
     * Endpoint: GET /v2/lingo-v2/lingo-content/word/:wordId
     * Returns the word payload or null on failure.
     */
    async fetchWordByName(wordName: string) {
      if (!wordName) return null;

      this.loader.fetchWord = true;
      try {
        const res = await api.get(`/v2/lingo-v2/lingo-content/find-word/${encodeURIComponent(wordName)}`);
        if (res.status === 200 && res.data) {
          const payload = (res.data.data ?? res.data) as any;
          // cache result
          this.wordCache = { ...(this.wordCache || {}), [wordName]: payload };
          return payload;
        } else {
          throw new Error("Failed to fetch word data");
        }
      } catch (err) {
        console.error("Error fetching word data:", err);
        return null;
      } finally {
        this.loader.fetchWord = false;
      }
    },

    /**
     * Fetch lingo engagement time split into classwork/homework for multiple students
     */
    async fetchLingoEngagementTime(payload: {
      school: string;
      studentIds: string[];
      startDate: string;
      endDate: string;
      timezone: string;
    }) {
      try {
        const response = await api.post(
          "/v2/data/lingo-engagements/fetch-lingo-time",
          payload
        );

        if (response.status === 200 && response.data.success) {
          const map: Record<string, { classworkSeconds: number; homeworkSeconds: number }> = {};
          for (const row of response.data.data) {
            map[row.uid] = {
              classworkSeconds: row.classworkSeconds || 0,
              homeworkSeconds: row.homeworkSeconds || 0,
            };
          }
          this.lingoEngagementTime = map;
        } else {
          this.lingoEngagementTime = {};
        }
      } catch (error) {
        console.error("Error fetching lingo engagement time:", error);
        this.lingoEngagementTime = {};
      }
    },

    /**
     * Fetch all custom lists for a school
     */
    async fetchCustomLists(schoolId: string) {
      this.loader.fetchCustomLists = true;

      try {
        const response = await api.get<CustomListResponse>(
          `/v2/lingo-v2/custom-lists?schoolId=${encodeURIComponent(schoolId)}`
        );

        if (response.status === 200 && response.data.success) {
          this.customLists = response.data.data;
        } else {
          throw new Error("Failed to fetch custom lists.");
        }
      } catch (error) {
        console.error("Error fetching custom lists:", error);
        this.customLists = [];
      } finally {
        this.loader.fetchCustomLists = false;
      }
    },

    /**
     * Generate a custom word list using AI
     */
    async generateCustomList(payload: GenerateCustomListPayload): Promise<GenerateCustomListData | null> {
      this.loader.generateCustomList = true;
      try {
        const response = await api.post<GenerateCustomListResponse>(
          "/v2/lingo-v2/custom-lists/generate",
          payload
        );
        if (response.status === 200 && response.data.success) {
          return response.data.data;
        } else {
          throw new Error("Failed to generate custom list.");
        }
      } catch (error) {
        console.error("Error generating custom list:", error);
        return null;
      } finally {
        this.loader.generateCustomList = false;
      }
    },

    /**
     * Save a custom list to the database
     */
    async saveCustomList(payload: SaveCustomListPayload): Promise<CustomList | null> {
      this.loader.saveCustomList = true;
      try {
        const response = await api.post<SaveCustomListResponse>(
          "/v2/lingo-v2/custom-lists",
          payload
        );
        if ((response.status === 200 || response.status === 201) && response.data.success) {
          this.customLists.unshift(response.data.data);
          return response.data.data;
        } else {
          throw new Error("Failed to save custom list.");
        }
      } catch (error) {
        console.error("Error saving custom list:", error);
        return null;
      } finally {
        this.loader.saveCustomList = false;
      }
    },

    /**
     * Fetch paginated students + per-student status for a custom list.
     */
    /**
     * Poll the lightweight status endpoint for a custom list.
     * Returns the current status + wordReadiness (no student data).
     */
    async fetchListStatus(listId: string): Promise<CustomListStatusData | null> {
      try {
        const response = await api.get<CustomListStatusResponse>(
          `/v2/lingo-v2/custom-lists/${encodeURIComponent(listId)}/status`
        );
        if (response.status === 200 && response.data.success) {
          // Also update the local customLists entry so the info card stays fresh
          const idx = this.customLists.findIndex(l => l._id === listId);
          if (idx !== -1) {
            this.customLists[idx] = {
              ...this.customLists[idx],
              status: response.data.data.status,
            };
          }
          return response.data.data;
        }
        return null;
      } catch (error) {
        console.error("Error polling list status:", error);
        return null;
      }
    },

    /**
     * Re-drive preparation for the words that did not finish (NLM-Test #84).
     *
     * Before this there was no retry of any kind: a list whose word generation
     * failed was stuck permanently and could only be recovered by hand-editing the
     * database. Only non-ready words are re-queued, so a retry never regenerates
     * content that already succeeded.
     */
    async retryListPreparation(listId: string, schoolId: string): Promise<{ ok: boolean; message?: string }> {
      try {
        const response = await api.post(
          `/v2/lingo-v2/custom-lists/${encodeURIComponent(listId)}/retry`,
          { schoolId }
        );
        if ((response.status === 202 || response.status === 200) && response.data?.success) {
          return { ok: true };
        }
        return { ok: false, message: response.data?.message || "We couldn't start that. Please try again in a moment." };
      } catch (error: any) {
        console.error("Error retrying list preparation:", error);
        return {
          ok: false,
          message: error?.response?.data?.message || "We couldn't start that. Please try again in a moment."
        };
      }
    },

    async fetchListStudents(listId: string, filters: AssignListStudentsFilters) {
      this.loader.fetchListStudents = true;
      try {
        const params = new URLSearchParams();
        params.set("schoolId", filters.schoolId);
        if (filters.classGroupId) params.set("classGroupId", filters.classGroupId);
        if (filters.grade !== undefined && filters.grade !== "") {
          params.set("grade", String(filters.grade));
        }
        if (filters.search) params.set("search", filters.search);
        if (filters.page) params.set("page", String(filters.page));
        if (filters.limit) params.set("limit", String(filters.limit));
        if (filters.sort) params.set("sort", filters.sort);

        const response = await api.get<AssignListStudentsResponse>(
          `/v2/lingo-v2/custom-lists/${encodeURIComponent(listId)}/students?${params.toString()}`
        );

        if (response.status === 200 && response.data.success) {
          this.listStudents = response.data.data.students;
          this.listStudentsMeta = {
            total: response.data.data.total,
            page: response.data.data.page,
            limit: response.data.data.limit,
            isLastPage: response.data.data.isLastPage,
          };
        } else {
          throw new Error("Failed to fetch list students.");
        }
      } catch (error) {
        console.error("Error fetching list students:", error);
        this.listStudents = [];
        this.listStudentsMeta = { total: 0, page: 1, limit: 50, isLastPage: true };
      } finally {
        this.loader.fetchListStudents = false;
      }
    },

    mergeUpdatedStatuses(updated: AssignUpdatedStatus[]) {
      const map = new Map(updated.map(u => [u.studentId, u.status]));
      this.listStudents = this.listStudents.map(s => {
        const next = map.get(s.uid);
        return next ? { ...s, status: next } : s;
      });
    },

    /**
     * Assign / reassign a custom list to a set of students.
     */
    async assignList(listId: string, studentIds: string[]): Promise<boolean> {
      if (!studentIds.length) return false;
      this.loader.assignList = true;
      try {
        const response = await api.post<AssignActionResponse>(
          `/v2/lingo-v2/custom-lists/${encodeURIComponent(listId)}/assign`,
          { studentIds }
        );
        if (response.status === 200 && response.data.success) {
          this.mergeUpdatedStatuses(response.data.data.updatedStatuses);
          return true;
        }
        throw new Error("Failed to assign list.");
      } catch (error) {
        console.error("Error assigning list:", error);
        return false;
      } finally {
        this.loader.assignList = false;
      }
    },

    /**
     * Unassign a custom list from a set of students.
     */
    async unassignList(listId: string, studentIds: string[]): Promise<boolean> {
      if (!studentIds.length) return false;
      this.loader.unassignList = true;
      try {
        const response = await api.post<AssignActionResponse>(
          `/v2/lingo-v2/custom-lists/${encodeURIComponent(listId)}/unassign`,
          { studentIds }
        );
        if (response.status === 200 && response.data.success) {
          this.mergeUpdatedStatuses(response.data.data.updatedStatuses);
          return true;
        }
        throw new Error("Failed to unassign list.");
      } catch (error) {
        console.error("Error unassigning list:", error);
        return false;
      } finally {
        this.loader.unassignList = false;
      }
    },

    /**
     * Clear the store
     */
    clearLingoData() {
      this.studentLingoData = [];
      this.lingoEngagementTime = {};
      this.customLists = [];
      this.listStudents = [];
      this.listStudentsMeta = { total: 0, page: 1, limit: 50, isLastPage: true };
    },
  },

  persist: true,
});